/**
 * HOW a conflicting statement reached the call (owner 2026-10-20: conflict
 * detection reads participant chat). The engine sets the sources by code from
 * the transcript line each quote sits on — `spoken` or `typed_chat` — never by
 * the model (api-ai-engine ConflictDetails.quoteSources / previousStatementSource
 * / newStatementSource). Absent sources (an older engine, a stored row) mean
 * "not recorded", and every reader then behaves exactly as before.
 */
import type { ConflictDetails } from '@/store/slices/aiAnalysisTypes';

export type StatementSource = 'spoken' | 'typed_chat';
type Side = 'previous' | 'new';

export function statementSource(details: ConflictDetails | null | undefined, side: Side): StatementSource | null {
  const value = side === 'previous' ? details?.previousStatementSource : details?.newStatementSource;
  return value === 'spoken' || value === 'typed_chat' ? value : null;
}

export function statementSourceLabel(source: StatementSource | null): string | null {
  if (source === 'typed_chat') return 'Typed in chat';
  if (source === 'spoken') return 'Spoken';
  return null;
}

/**
 * The typed message behind one side of the conflict, exactly as typed, or null
 * when that side was not typed or the engine did not establish which message it
 * was. The engine carries the FULL message (`previousSourceMessage` /
 * `newSourceMessage`); failing that, the quote it names as backing that side
 * (`previousQuoteIndex` / `newQuoteIndex`) when that quote sits on a typed line.
 * Never inferred from where quotes sit in the list: a card must not show a typed
 * message that is not the statement beside it.
 */
export function typedChatMessage(details: ConflictDetails | null | undefined, side: Side): string | null {
  if (statementSource(details, side) !== 'typed_chat') return null;
  const message = side === 'previous' ? details?.previousSourceMessage : details?.newSourceMessage;
  if (typeof message === 'string' && message.trim()) return message;
  const index = side === 'previous' ? details?.previousQuoteIndex : details?.newQuoteIndex;
  const quotes = Array.isArray(details?.transcriptQuotes) ? details!.transcriptQuotes : [];
  const sources = Array.isArray(details?.quoteSources) ? details!.quoteSources : [];
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= quotes.length) return null;
  const quote = quotes[index];
  return sources[index] === 'typed_chat' && typeof quote === 'string' && quote ? quote : null;
}

/** The pinned follow-up note's conflict lines; unchanged when no source is recorded. */
export function conflictFollowUpNote(details: ConflictDetails | null | undefined): string {
  if (!details) return '';
  const tag = (side: Side) => {
    const source = statementSource(details, side);
    return source === 'typed_chat' ? ' (typed in chat)' : source === 'spoken' ? ' (spoken)' : '';
  };
  let note = '';
  if (details.previousStatement) note += `\n\nOriginal${tag('previous')}: "${details.previousStatement}"`;
  if (details.newStatement) note += `\nContradicting${tag('new')}: "${details.newStatement}"`;
  return note;
}
